import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    ERROR_CONVERSATION_EXISTS,
    ERROR_TITLE_GENERIC,
    ERROR_TRY_AGAIN,
    ERROR_USER_DOES_NOT_EXIST,
    ERROR_USER_DOES_NOT_EXIST_SHORT
} from "@/constants/errors";
import { USER_ADDRESS_TYPE } from "@/constants/message";
import { Group, User } from "../../types/users";
import {
  NEW_CONVERSATION_ACTION,
  NEW_CONVERSATION_DESCRIPTION,
  NEW_CONVERSATION_TITLE,
} from "../../constants/ui";
import {
  addConversation,
  selectConversations,
} from "@/features/control/controlSlice";
import { getGroups, getUsers } from "@/services/userGroupService";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { Conversation } from "../../types/conversation";
import { PenSquare } from "lucide-react";
import SearchBar from "./SearchBar";
import { ToastAction } from "@/components/ui/toast";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  normalizeExternalAddress,
  resolveExternalDmAddressType,
} from "@/utils/addresses";

const NewConversationDialog = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [newConversation, setNewConversation] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const handleAddConversation = () => {
    // Groups are intentionally excluded — users cannot start new group conversations.
    // const matchedGroup: Group | undefined = (groups as Group[]).find(
    //   (g: Group) =>
    //     g.name.toLowerCase() === newConversation.toLowerCase().trim()
    // );

    const matchedUser: User | undefined = (users as User[]).find(
      (u: User) =>
        u.username.toLowerCase() === newConversation.toLowerCase().trim()
    );

    // An external address has no local user to match against, so its shape is all we can check.
    // The router re-validates with the matching gateway and only resolves the actual recipient
    // when the message is relayed — an unknown remote user or undeliverable mailbox surfaces
    // as a failed send, not here.
    const externalAddress = normalizeExternalAddress(newConversation);
    const externalAddressType = matchedUser
      ? null
      : resolveExternalDmAddressType(externalAddress);

    // TODO: Make conversation with this user or group the selected convo
    if (
      conversations.find(
        (c: Conversation) =>
          c.address === (externalAddressType ? externalAddress : newConversation) ||
          c.groupName === newConversation
      )
    ) {
      setError(ERROR_CONVERSATION_EXISTS);
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: ERROR_CONVERSATION_EXISTS,
        action: (
          <ToastAction
            onClick={() => {
              setNewConversation("");
              setError("");
            }}
            altText={ERROR_TRY_AGAIN}
          >
            {ERROR_TRY_AGAIN}
          </ToastAction>
        ),
      });
      return;
    }

    // If there is no matching user and it isn't an external address, throw error
    if (!matchedUser && !externalAddressType) {
      setError(ERROR_USER_DOES_NOT_EXIST);
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: ERROR_USER_DOES_NOT_EXIST_SHORT,
        action: (
          <ToastAction
            onClick={() => {
              setNewConversation("");
              setError("");
            }}
            altText={ERROR_TRY_AGAIN}
          >
            {ERROR_TRY_AGAIN}
          </ToastAction>
        ),
      });
      return;
    }

    // Groups are intentionally excluded — users cannot start new group conversations.
    // if (matchedGroup) {
    //   dispatch(
    //     addConversation({
    //       addressType: GROUP_ADDRESS_TYPE,
    //       address: matchedGroup.address,
    //       addressId: matchedGroup.id,
    //       fullname: `${matchedGroup.name} ${matchedGroup.description}`,
    //       groupName: matchedGroup.name,
    //       ownerName: matchedGroup.ownerName,
    //       type: "Group",
    //       updated: "",
    //     })
    //   );
    // } else if (matchedUser) {
    if (externalAddressType) {
      // No sm_addr row exists until the router handles the first send, so addressId 0 stands in
      // until resolveConversationAddressId fills it. fullname is the address itself — the
      // router reports these threads the same way, and ConversationList matches rows by it.
      dispatch(
        addConversation({
          addressType: externalAddressType,
          address: externalAddress,
          addressId: 0,
          fullname: externalAddress,
          groupName: "",
          type: "User",
          updated: "",
        })
      );
    } else if (matchedUser) {
      // Else, add user to conversation list
      const name = [matchedUser.firstname, matchedUser.lastname]
        .filter(Boolean)
        .join(" ");
      const fullname = name
        ? `${matchedUser.username} (${name})`
        : matchedUser.username;

      dispatch(
        addConversation({
          addressType: USER_ADDRESS_TYPE,
          address: matchedUser.username,
          addressId: matchedUser.id,
          fullname: fullname,
          groupName: "",
          type: "User",
          updated: "",
        })
      );
    }
    setIsDialogOpen(false);
    setNewConversation("");
    setError("");
  };

  /**
   * Handler function to manually close dialog component
   */
  const onCancel = () => {
    setIsDialogOpen(false);
    setError("");
  };

  /**
   * Handler function to update state that holds input value
   */
  const onSelectConversation = (name: string) => {
    setNewConversation(name);
    setError("");
  };

  /**
   * Handler function to manually opens dialog component and retrieve group/user data
   */
  const onOpen = () => {
    fetchGroupsAndUsers();
    setNewConversation("");
    setIsDialogOpen(true);
  };

  /**
   * Helper function to retrieve group and user data using API wrappers
   */
  const fetchGroupsAndUsers = async () => {
    try {
      const groups = await getGroups();
      const users = await getUsers();
      setGroups(groups);
      setUsers(users);
    } catch (_) {
      console.error("Cannot load groups and/or users");
    }
  };

  return (
    <>
        <AlertDialog open={isDialogOpen}>
          <AlertDialogTrigger onClick={() => onOpen()}>
            <PenSquare data-testid="new-conversation-icon" size={20} />
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{NEW_CONVERSATION_TITLE}</AlertDialogTitle>
              <AlertDialogDescription>
                {NEW_CONVERSATION_DESCRIPTION}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <SearchBar
              userList={users}
              groupList={groups}
              onSelectConversation={onSelectConversation}
            />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => onCancel()}>
                Close
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleAddConversation()}
                disabled={error.length > 0 || newConversation.length === 0}
              >
                {NEW_CONVERSATION_ACTION}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </>
  );
};

export default NewConversationDialog;
